import { createSharedComposable, useWindowSize } from "@vueuse/core";
import { type Ref, computed } from "vue";

type Breakpoint = "xs" | "sm" | "md" | "lg" | "xl" | "xl2" | "xl3";

type OnlyBreakpoint = `${Breakpoint}Only`;
type OrLowerBreakpoint = `${Breakpoint}OrLower`;

const screens: Record<Breakpoint, number> = {
	xs: 0,
	sm: 640,
	md: 768,
	lg: 1024,
	xl: 1280,
	xl2: 1536,
	xl3: 2400,
};

export const useScreenSize = createSharedComposable(() => {
	const { width } = useWindowSize();

	const breakpoints: Record<Breakpoint, Readonly<Ref<boolean>>> = {
		xl3: computed(() => screens.xl2 <= width.value),
		xl2: computed(() => screens.xl <= width.value),
		xl: computed(() => screens.lg <= width.value),
		lg: computed(() => screens.md <= width.value),
		md: computed(() => screens.sm <= width.value),
		sm: computed(() => screens.xs <= width.value),
		xs: computed(() => true),
	} as const;

	function isOnly(breakpoint: Breakpoint) {
		return (
			breakpoints[breakpoint].value &&
			Object.values(breakpoints).filter((v) => v.value).length === 1
		);
	}

	function orLower(breakpoint: Breakpoint) {
		const keys = Object.keys(breakpoints);
		const index = keys.findIndex((k) => k === breakpoint);
		if (index === -1) return false;

		return Object.values(breakpoints)
			.slice(0, index)
			.every((v) => !v.value);
	}

	const onlyBreakpoints: Record<OnlyBreakpoint, Readonly<Ref<boolean>>> = {
		xl3Only: computed(() => isOnly("xl3")),
		xl2Only: computed(() => isOnly("xl2")),
		xlOnly: computed(() => isOnly("xl")),
		lgOnly: computed(() => isOnly("lg")),
		mdOnly: computed(() => isOnly("md")),
		smOnly: computed(() => isOnly("sm")),
		xsOnly: computed(() => isOnly("xs")),
	};

	const orLowerBreakpoints: Record<OrLowerBreakpoint, Readonly<Ref<boolean>>> = {
		xl3OrLower: computed(() => orLower("xl3")),
		xl2OrLower: computed(() => orLower("xl2")),
		xlOrLower: computed(() => orLower("xl")),
		lgOrLower: computed(() => orLower("lg")),
		mdOrLower: computed(() => orLower("md")),
		smOrLower: computed(() => orLower("sm")),
		xsOrLower: computed(() => orLower("xs")),
	};

	const currentSize = computed<Breakpoint>(() => {
		return (
			(Object.entries(breakpoints).find(([_, value]) => value.value)?.[0] as
				| Breakpoint
				| undefined) || "xl3"
		);
	});

	return {
		...breakpoints,
		...onlyBreakpoints,
		...orLowerBreakpoints,
		currentSize,
	};
});
